// ProgressBar — how far through a flow you are. Presentation-only, canonical
// tokens only: --border track, --cta fill.
//
// The continuous bar is 6px (h-1.5). At 4px it read as a hairline artefact of
// the card edge rather than as progress; 6px is legible at a glance without
// becoming a UI element competing with the content. The segmented shape stays
// at 2px: its gaps already carry the reading, and thickening it makes the
// segments look like buttons.
//
// The transition is INLINE, not Tailwind's `transition-transform`: v4 compiles
// that to `transition-property: transform, translate, scale, rotate`, and paired
// with an inline `transform` the browser fires no transition at all. Measured:
// a pure-inline transition was mid-flight at 200ms while the Tailwind-classed
// twin sat at its start value with zero transitionrun events.
//
// It fills by SCALING, not by growing: `width` is a layout property and the two
// continuous fills animated it, so every tick relaid out and repainted the bar.
// `scaleX` from a left origin is the same picture on the GPU. The bar carries no
// text and no children, so there is nothing for the scale to distort.
//
// Both continuous fills also ran on a bare `duration-300 ease-out` — Tailwind's
// cubic-bezier(0, 0, 0.2, 1), materially weaker than the DS curve — while the
// segmented fill three functions up used the tokens. One component, two motion
// specs, and the odd one out was the one people actually see.
//
// The fill is --cta, the action colour itself, so the bar and the button
// driving it are the same coral. It rode --cta-hover while THAT was the coral;
// after the flip that would have drawn progress in a hover state, a shade off
// every button on the same screen.
//
// Deliberately NOT --success, because a half-finished wizard has not succeeded
// at anything, and not --interactive, which names focus and selection —
// progress is neither. All three fills carry the same token, so the system
// draws progress in one colour whichever shape a flow uses.
//
// TWO SHAPES, because the product genuinely draws two:
//
//   continuous  one line filling left→right. Rendered full-bleed (flush to a
//               StepShell's top edge) it reads as a growing top border.
//   segmented   one bar PER STEP with gaps, each filled or not. Couple
//               Onboarding uses this — with few enough steps that the count is
//               worth showing, the segments say "4 of 7" without a label.
//
// Couple Onboarding had its own copy of the segmented one, on `bg-[#363230]`
// and `bg-border-disabled-subtle` — a hardcoded hex and a token that is not in
// this design system. That is the thing this component exists to stop, so the
// shape moved in here rather than the hex being tidied in place.
//
// `frontend/src/survey` keeps its own: it is on the older `wren` token family,
// draws a "N of M" label the flows do not, and is not one of the four surfaces
// this system covers.

export type ProgressBarShape = "continuous" | "segmented";

export interface ProgressBarProps {
    /** 0-indexed position of the current step within the progress range. */
    currentStep?: number;
    totalSteps?: number;
    percent?: number;
    /** Defaults to `continuous`. `segmented` needs currentStep + totalSteps. */
    shape?: ProgressBarShape;
    /** The unfilled --border groove behind the fill. On by default.
     *
     *  Off leaves the coral with no rail to run on: the fill alone, on whatever
     *  the bar is sitting on. Used where the bar hangs directly beneath a solid
     *  block (the setup wizard's dark header), and a light groove would read as
     *  a third stripe between the block and the content rather than as the
     *  unfinished part of a bar. Segmented ignores this: its gaps ARE the
     *  groove, and an unfilled segment with no ground is just missing. */
    track?: boolean;
}

export function ProgressBar({
    currentStep,
    totalSteps,
    percent,
    shape = "continuous",
    track = true,
}: ProgressBarProps) {
    const groove = track ? "bg-border" : "";
    if (shape === "segmented" && currentStep !== undefined && totalSteps !== undefined) {
        return (
            <div
                role="progressbar"
                aria-valuenow={currentStep + 1}
                aria-valuemin={1}
                aria-valuemax={totalSteps}
                className="flex h-0.5 w-full gap-1"
            >
                {Array.from({ length: totalSteps }).map((_, i) => (
                    <div
                        key={i}
                        className={`flex-1 rounded-full transition-colors duration-[var(--dur-base)] ease-[var(--ease-out)] ${
                            i <= currentStep ? "bg-cta" : "bg-border"
                        }`}
                    />
                ))}
            </div>
        );
    }

    if (percent !== undefined) {
        const pct = Math.max(0, Math.min(100, percent));
        return (
            <div
                role="progressbar"
                aria-valuenow={Math.round(pct)}
                aria-valuemin={0}
                aria-valuemax={100}
                className={`h-1.5 w-full ${groove}`}
            >
                <div
                    className="h-full w-full origin-left bg-cta"
                    style={{
                        transform: `scaleX(${pct / 100})`,
                        transition: "transform var(--dur-base) var(--ease-out)",
                    }}
                />
            </div>
        );
    }

    const resolvedCurrentStep = currentStep ?? 0;
    const resolvedTotalSteps = totalSteps ?? 0;
    const pct =
        resolvedTotalSteps > 0
            ? Math.max(0, Math.min(100, ((resolvedCurrentStep + 1) / resolvedTotalSteps) * 100))
            : 0;

    // Keep the ARIA range coherent even when totalSteps <= 0: clamp the max to
    // at least 1 and pin valuenow within [valuemin, valuemax].
    const valueMax = Math.max(resolvedTotalSteps, 1);
    const valueNow = Math.max(1, Math.min(resolvedCurrentStep + 1, valueMax));

    return (
        <div
            role="progressbar"
            aria-valuenow={valueNow}
            aria-valuemin={1}
            aria-valuemax={valueMax}
            className={`h-1.5 w-full ${groove}`}
        >
            <div
                className="h-full w-full origin-left bg-cta"
                style={{
                    transform: `scaleX(${pct / 100})`,
                    transition: "transform var(--dur-base) var(--ease-out)",
                }}
            />
        </div>
    );
}
